/* Advisory only. Warehouse authority stays in AA; no cart or checkout writes. */
(function () {
  'use strict';
  const choices = {
    ab: { path: '/collections/benders-dekorstein', name: 'Benders', link: 'Se Benders dekorstein' },
    d: { path: '/collections/miljostein-dekorstein', name: 'Miljøstein', link: 'Se Miljøstein dekorstein' }
  };
  function canonical(value) {
    if (Array.isArray(value)) return '[' + value.map(canonical).join(',') + ']';
    if (value && typeof value === 'object') return '{' + Object.keys(value).sort().map(key => JSON.stringify(key) + ':' + canonical(value[key])).join(',') + '}';
    return JSON.stringify(value);
  }
  function wire(cart) {
    if (!cart || !Array.isArray(cart.items)) throw Error('Invalid cart');
    return cart.items.map(item => {
      const properties = {};
      Object.keys(item.properties || {}).sort().forEach(key => {
        if (typeof item.properties[key] === 'string') properties[key] = item.properties[key];
      });
      const line = {
        line_key: item.key, variant_id: String(item.variant_id || ''), sku: item.sku,
        quantity: item.quantity, properties,
        selling_plan_id: String(item.selling_plan_allocation?.selling_plan?.id || '')
      };
      if (typeof line.line_key !== 'string' || !line.line_key || !/^\d+$/.test(line.variant_id) || typeof line.sku !== 'string' || !line.sku || !Number.isSafeInteger(line.quantity) || line.quantity <= 0) throw Error('Invalid line');
      return line;
    });
  }
  function group(value) {
    if (!value || value.schema !== 'aa-location-compatibility-v1' || !['EMPTY', 'COMPATIBLE'].includes(value.state) || value.operation_allowed !== true || value.checkout_allowed !== true || value.offer_allowed !== true || value.replacement_allowed !== false || !/^[0-9a-f]{64}$/.test(value.candidate_cart_fingerprint_id || '') || typeof value.registry_generation !== 'string' || !value.registry_generation || typeof value.inventory_generation !== 'string' || !value.inventory_generation) return null;
    const keys = value.common_location_keys;
    if (!Array.isArray(keys) || !keys.length || new Set(keys).size !== keys.length) return null;
    if (keys.every(key => key === 'sandefjord' || key === 'kongsvinger')) return 'ab';
    return keys.length === 1 && keys[0] === 'mysen' ? 'd' : null;
  }
  async function digest(value) {
    const bytes = new TextEncoder().encode(canonical(value));
    const hash = await crypto.subtle.digest('SHA-256', bytes);
    return Array.from(new Uint8Array(hash), byte => byte.toString(16).padStart(2, '0')).join('');
  }
  async function classify(cart, config, signal, request = fetch) {
    const lines = wire(cart);
    // Partition one real line: AA evaluates the exact existing quantities, never an added unit.
    // A shipping pallet can also be physical. Only the server may reject it as a candidate.
    const candidates = cart.items.map((item, index) => item.requires_shipping === true ? index : -1).filter(index => index >= 0).slice(0, 3);
    for (const index of candidates) {
      const selected = lines[index];
      const remaining = lines.filter((_, i) => i !== index);
      const incoming = [{ variant_id: selected.variant_id, sku: selected.sku, quantity: selected.quantity }];
      const random = crypto.getRandomValues(new Uint8Array(12));
      const response = await request(new URL('api/aa/public/location-check', config.base).href, {
        method: 'POST', credentials: 'omit', cache: 'no-store', signal,
        headers: { 'Content-Type': 'application/json', 'X-AA-Operation-ID': 'aabg-' + Array.from(random, byte => byte.toString(16).padStart(2, '0')).join('') },
        body: JSON.stringify({ cart: remaining, incoming, surface: config.surface, qa_theme_id: config.theme })
      });
      if (!(response.headers.get('content-type') || '').toLowerCase().startsWith('application/json')) return null;
      const responseText = await response.text();
      if (responseText.length > 32768) return null;
      const value = JSON.parse(responseText);
      if (!response.ok) {
        if (value.error === 'PRODUCT_NOT_AVAILABLE') continue;
        return null;
      }
      const expected = await Promise.all([
        digest({ schema: 'aa-shopify-cart-preimage-v1', lines: remaining }),
        digest({ schema: 'aa-location-incoming-v1', lines: incoming })
      ]);
      if (value.cart_binding_id !== expected[0] || value.incoming_binding_id !== expected[1]) return null;
      return group(value);
    }
    return null;
  }
  if (typeof module === 'object' && module.exports) {
    module.exports = { canonical, wire, group, digest, classify };
    return;
  }
  const section = document.querySelector('[data-warehouse-guidance]');
  if (!section || section.dataset.guidanceBound) return;
  section.dataset.guidanceBound = 'true';
  const grid = section.querySelector('.subcollections__grid');
  const notice = section.querySelector('[data-category-guidance]');
  if (!grid || !notice) return;
  const original = Array.from(grid.children);
  const cards = {};
  Object.entries(choices).forEach(([key, choice]) => {
    const anchor = grid.querySelector('a[href="' + choice.path + '"]');
    if (!anchor) return;
    const badge = document.createElement('span');
    badge.className = 'category-guidance__badge';
    badge.textContent = 'Anbefalt for kurven din';
    badge.hidden = true;
    anchor.prepend(badge);
    cards[key] = { anchor, badge, wrapper: original.find(node => node === anchor || node.contains(anchor)) };
  });
  let revision = 0, abort, timer, pending = null, pointerDown = false, hovering = false;
  let lastSignature = '', lastChecked = 0, lastGroup = null;
  function clearAdvice() {
    notice.style.visibility = 'hidden';
    Object.values(cards).forEach(card => { card.anchor.classList.remove('is-cart-recommended'); card.badge.style.visibility = 'hidden'; });
  }
  function apply() {
    if (!pending || pointerDown || hovering || grid.contains(document.activeElement)) return;
    const key = pending.key;
    pending = null;
    clearAdvice();
    notice.hidden = true;
    Object.values(cards).forEach(card => { card.badge.hidden = true; card.badge.style.visibility = ''; });
    const card = cards[key];
    const order = card ? [card.wrapper, ...original.filter(node => node !== card.wrapper)] : original;
    order.forEach((node, index) => { if (grid.children[index] !== node) grid.insertBefore(node, grid.children[index] || null); });
    if (!card) return;
    const choice = choices[key];
    card.anchor.classList.add('is-cart-recommended');
    card.badge.hidden = false;
    notice.querySelector('[data-guidance-message]').textContent = 'Velg produkter merket ' + choice.name + '. Varer fra andre lagre må bestilles separat.';
    const link = notice.querySelector('[data-guidance-link]');
    link.href = choice.path; link.textContent = choice.link;
    notice.style.visibility = '';
    notice.hidden = false;
  }
  function config() {
    const root = document.querySelector('[data-msno-aa-fk7-v2][data-aa-enabled="true"]');
    if (!root || root.dataset.aaRuntimeCurrentness !== 'CURRENT') return null;
    const theme = root.dataset.aaThemeId;
    const currentTheme = String(window.Shopify?.theme?.id || '');
    if (!/^\d{10,20}$/.test(theme || '') || !currentTheme || theme !== currentTheme) return null;
    const surface = root.dataset.aaSurface;
    if (!['FK7_AA_QA', 'FK7_AA_PRODUCTION'].includes(surface)) return null;
    const base = new URL(root.dataset.aaApiBase);
    if (base.protocol !== 'https:' || base.username || base.password || base.search || base.hash) return null;
    if (!base.pathname.endsWith('/')) base.pathname += '/';
    return { base, theme, surface };
  }
  async function readCart(signal) {
    const base = window.Shopify?.routes?.root || '/';
    const response = await fetch(base + 'cart.js', { credentials: 'same-origin', cache: 'no-store', signal });
    if (!response.ok) throw Error('Cart unavailable');
    return response.json();
  }
  async function refresh(id) {
    const controller = new AbortController();
    abort = controller;
    const signal = controller.signal;
    const timeout = setTimeout(() => controller.abort(), 8000);
    try {
      const cart = await readCart(signal);
      const signature = canonical(wire(cart));
      if (id !== revision) return;
      const target = config();
      if ((target || !cart.items.length) && signature === lastSignature && Date.now() - lastChecked < 15000) {
        pending = { key: lastGroup }; apply(); return;
      }
      let key = null;
      if (cart.items.length && target) key = await classify(cart, target, signal);
      if (id !== revision) return;
      // Another tab may have written without dispatching a same-page event.
      if (cart.items.length && canonical(wire(await readCart(signal))) !== signature) { schedule(); return; }
      if (id !== revision) return;
      lastSignature = key || !cart.items.length ? signature : '';
      lastChecked = Date.now();
      lastGroup = key;
      pending = { key }; apply();
    } catch (_) {
      if (id === revision) { lastSignature = ''; pending = { key: null }; apply(); }
    } finally { clearTimeout(timeout); }
  }
  function schedule() {
    revision += 1;
    if (abort) abort.abort();
    clearTimeout(timer);
    pending = null;
    clearAdvice();
    const id = revision;
    timer = setTimeout(() => refresh(id), 180);
  }
  grid.addEventListener('pointerenter', event => { if (event.pointerType === 'mouse') hovering = true; });
  grid.addEventListener('pointerleave', () => { hovering = false; apply(); });
  grid.addEventListener('pointerdown', () => { pointerDown = true; });
  window.addEventListener('pointerup', () => { pointerDown = false; setTimeout(apply, 0); });
  window.addEventListener('pointercancel', () => { pointerDown = false; apply(); });
  grid.addEventListener('focusout', () => setTimeout(apply, 0));
  ['aa:storefront-bootstrap', 'aa:runtime-ready', 'aa:cart-readback', 'aa:cart-changed', 'aa:cart-reconciled', 'aa:cart-rehydrated'].forEach(name => document.addEventListener(name, () => { if (name === 'aa:storefront-bootstrap') lastSignature = ''; schedule(); }));
  window.addEventListener('pageshow', schedule);
  window.addEventListener('pagehide', () => { revision += 1; clearTimeout(timer); if (abort) abort.abort(); clearAdvice(); });
  document.addEventListener('visibilitychange', () => { if (!document.hidden) schedule(); });
  schedule();
}());
